import React, { useEffect, useState } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import { FaDownload, FaEnvelope, FaWhatsapp } from "react-icons/fa";
import { Link } from "react-router-dom"; // Make sure to place this in /src/assets or /public if needed

const Freelancer = () => {
  const [data, setData] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("All");

  useEffect(() => {
    AOS.init({ duration: 800, once: true });
    import("../data/freelancer.json")
      .then((module) => setData(module.default))
      .catch((err) => console.error("Error loading JSON:", err));
  }, []);

  if (!data)
    return (
      <div className="text-center text-gray-600 mt-10">Loading...</div>
    );

  const { name, bio, profilePic, services, reviews, pricing, contact, resumeLink } = data;

  const categories = ["All", ...new Set(services.map((s) => s.category))];
  const filteredServices =
    selectedCategory === "All"
      ? services
      : services.filter((s) => s.category === selectedCategory);

  return (
    <div className="bg-[#f7f9fc] text-[#1a1a1a] min-h-screen font-sans">
      <div className="absolute top-6 left-6 z-20 flex items-center gap-3">
        <Link to="/templates" className="flex items-center space-x-3">
        <img src="/logo.png" alt="Project Port Logo" className="h-12 w-12 drop-shadow-lg" />
        <span className="text-xl font-bold text-black">Project Port</span>
        </Link>
      </div>
     
      {/* HERO */}
      <section className="flex flex-col md:flex-row items-center justify-between px-6 md:px-20 py-12 gap-10">
        <div className="text-center md:text-left md:w-1/2" data-aos="fade-right">
          <h1 className="text-4xl md:text-5xl font-extrabold text-[#2563eb] mb-4">{name}</h1>
          <p className="text-[#4b5563] text-lg leading-relaxed">{bio}</p>
          <div className="flex flex-col sm:flex-row gap-4 mt-6">
            <a
              href={resumeLink}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#2563eb] text-white font-bold py-2 px-6 rounded-full hover:bg-[#1e40af] flex items-center justify-center gap-2 transition duration-300"
            >
              <FaDownload /> Download Resume
            </a>
            <a
              href={`mailto:${contact}`}
              className="border border-[#2563eb] text-[#2563eb] font-bold py-2 px-6 rounded-full hover:bg-[#2563eb] hover:text-white flex items-center justify-center gap-2 transition duration-300"
            >
              <FaEnvelope /> Email Me
            </a>
            <a
              href={`https://wa.me/${contact.replace(/[^0-9]/g, "")}`}
              target="_blank"
              className="border border-green-600 text-green-600 font-bold py-2 px-6 rounded-full hover:bg-green-600 hover:text-white flex items-center justify-center gap-2 transition duration-300"
            >
              <FaWhatsapp /> Chat
            </a>
          </div>
        </div>
        <div className="md:w-1/2 flex justify-center" data-aos="fade-left">
          <img
            src={profilePic}
            alt="Freelancer"
            className="w-48 h-48 md:w-64 md:h-64 rounded-full border-4 border-[#2563eb] shadow-lg"
          />
        </div>
      </section>

      {/* SERVICES */}
      <section className="px-6 md:px-20 py-10">
        <h2 className="text-3xl font-bold text-[#2563eb] mb-6" data-aos="fade-up">Services</h2>

        {/* Category Filter */}
        <div className="flex gap-3 mb-8 flex-wrap">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-4 py-1 rounded-full border ${
                selectedCategory === cat
                  ? "bg-[#2563eb] text-white border-[#2563eb]"
                  : "bg-transparent text-[#2563eb] border-[#2563eb]"
              } transition-all duration-300`}
            >
              {cat}
            </button>
          ))}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredServices.map((service, i) => (
            <div
              key={i}
              data-aos="zoom-in"
              className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition-transform border border-gray-200 hover:scale-105"
            >
              <h3 className="text-xl font-semibold mb-2 text-[#1a1a1a]">{service.title}</h3>
              <p className="text-[#4b5563]">{service.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PRICING */}
      <section className="px-6 md:px-20 py-10 bg-[#eef1f6]">
        <h2 className="text-3xl font-bold text-[#2563eb] mb-6" data-aos="fade-up">Pricing Plans</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {pricing.map((plan, i) => (
            <div
              key={i}
              data-aos="fade-up"
              className="relative bg-white p-6 rounded-lg border border-gray-300 shadow-sm hover:shadow-md transition"
            >
              {plan.tag && (
                <span className="absolute top-2 right-2 bg-[#2563eb] text-white text-xs px-2 py-1 rounded-full">
                  {plan.tag}
                </span>
              )}
              <h3 className="text-2xl font-bold mb-2">{plan.title}</h3>
              <p className="text-xl text-[#2563eb] mb-4">{plan.price}</p>
              <p className="text-[#4b5563]">{plan.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* REVIEWS */}
      <section className="px-6 md:px-20 py-10">
        <h2 className="text-3xl font-bold text-[#2563eb] mb-6" data-aos="fade-up">What Clients Say</h2>
        <div className="space-y-6">
          {reviews.map((review, i) => (
            <div
              key={i}
              data-aos="fade-up"
              className="bg-white p-6 rounded-lg border-l-4 border-[#2563eb] shadow-sm"
            >
              <p className="text-[#4b5563] italic">“{review.text}”</p>
              <p className="text-[#2563eb] mt-3 font-semibold">– {review.author}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FOOTER */}
      <footer className="text-center py-8 border-t border-gray-200 mt-10 text-gray-500 text-sm">
        <p>
          📧 Contact:{" "}
          <a href={`mailto:${contact}`} className="text-[#2563eb] underline">
            {contact}
          </a>
        </p>
        <p className="mt-2">
          © {new Date().getFullYear()} {name} — Freelancer Portfolio
        </p>
      </footer>
    </div>
  );
};

export default Freelancer;
